import { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import Parser from 'html-react-parser';
import classNames from 'classnames';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import MenuItem from '@mui/material/MenuItem';
import Card from '@mui/material/Card';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import FilterListIcon from '@mui/icons-material/FilterList';
import ViewComfyAltIcon from '@mui/icons-material/ViewComfyAlt';
import ComputerIcon from '@mui/icons-material/Computer';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import PlaylistRemoveIcon from '@mui/icons-material/PlaylistRemove';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import FeedIcon from '@mui/icons-material/Feed';
import ReceiptIcon from '@mui/icons-material/Receipt';
import Radio from '@mui/material/Radio';
import Tooltip from '@mui/material/Tooltip';
import Popover from '@mui/material/Popover';
import MenuList from '@mui/material/MenuList';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { debounce } from 'lodash';

import Button, { IconButton } from '../Button';
import Pagination from '../Pagination';
import Loading from '../Loading';
import {
  trackingNotificationType,
  notificationStatusType,
} from '../../constants';
import useNotification from '../../hooks/useNotification';
import { useAppSelector, useAppDispatch } from '../../redux/hooks';
import { getColumnColor } from '../../utils/helpers';
import { formatDisplayNumber } from '../../utils/format';
import { parseDate } from '../../utils/date';
import { ROUTES } from '../../constants/routes';
import { setCloseNotificationDrawer } from './NotificationSlice';
import {
  transferNotificationData,
  getTotalNotificationByFollowCategory,
} from './NotificationDrawer.helpers';
import WrapperBookmark from '../../pages/Dashboard/components/WrapperBookmark';
import useInvestmentCategories from '../../hooks/useInvestmentCategories';
import {
  FinancialChart,
  renderContentItem,
  renderHighlight,
  getTotalFollow,
  getMinMaxZizZag,
} from '../../pages/ManageInvestments/components/Notification';
import useToggleTheme from '../../hooks/useToggleTheme';

import * as Styles from './Notification.styles';
import CustomSnackbar from '../Snackbar/Snackbar';
import Input from '../Input';
import NotificationItem from './Views/NotificationItem';

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <Styles.TabPanel
      className="pt-2"
      role="tabpanel"
      hidden={value !== index}
      id={`notification-tabpanel-${index}`}
      aria-labelledby={`notification-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box className="tab-section">
          <Box className="mt-2 tab-main-content relative">{children}</Box>
        </Box>
      )}
    </Styles.TabPanel>
  );
}

function a11yProps(index) {
  return {
    id: `notification-tab-${index}`,
    'aria-controls': `notification-tabpanel-${index}`,
  };
}

export function getICon(type) {
  switch (type) {
    case trackingNotificationType.companyExercise:
      return (
        <div className="icon-wrapper">
          <ReceiptIcon />
          <Typography component="span" className="menu-text ml-1">
            Thực hiện quyền
          </Typography>
        </div>
      );
    case trackingNotificationType.priceChange:
    case trackingNotificationType.Warning:
      return (
        <div className="icon-wrapper">
          <WarningAmberIcon />
          <Typography component="span" className="menu-text ml-1">
            Cảnh báo
          </Typography>
        </div>
      );
    case trackingNotificationType.newsUpdate:
      return (
        <div className="icon-wrapper">
          <FeedIcon />
          <Typography component="span" className="menu-text ml-1">
            Tin tức
          </Typography>
        </div>
      );
    default:
      return (
        <div className="icon-wrapper">
          <ComputerIcon />
          <Typography component="span" className="menu-text ml-1">
            Thông báo
          </Typography>
        </div>
      );
  }
}

function GeneralNotification({
  notification,
  otherInfo,
  currentTab,
  selectedFollowStock,
  selectedInvestmentStock,
}) {
  if (currentTab === 0) {
    if (selectedInvestmentStock !== 'general') {
      return (
        <Card variant="outlined" className="w-full">
          <Box className="notification-header">
            {getICon(notification.type)}
            <div className="ml-auto">{notification.timeSend}</div>
          </Box>
          <Box className="ml-3 notification-content">
            <ul>
              {otherInfo.investments.map((investment) => (
                <li key={investment.machungkhoan} className="mb-2">
                  {investment.change > 0 ? (
                    <div>
                      <b>{investment.machungkhoan}</b>: tăng{' '}
                      <b style={{ color: getColumnColor(investment.change) }}>
                        +{formatDisplayNumber(investment.change)}đ( +
                        {investment.percent}%)
                      </b>{' '}
                      trên tổng giá trị đầu tư vào cổ phiếu, nhà đầu tư nên theo
                      dõi những biến động trong phiên để có những quyết định phù
                      hợp.{' '}
                      {investment.zigZag.length && investment.percent > 4 ? (
                        <>
                          Theo dữ liệu giao dịch trong <b>12 tháng</b> gần nhất
                          chỉ có <b>{investment.zigZag.length}</b> lần{' '}
                          <b>{investment.machungkhoan}</b> tăng vượt mức{' '}
                          <b>
                            {getMinMaxZizZag(investment.zigZag)[0].toFixed(2)}%
                          </b>{' '}
                          theo giá đóng cửa vào các chu kỳ:
                        </>
                      ) : null}
                    </div>
                  ) : (
                    <p>
                      <b>{investment.machungkhoan}</b>: giảm{' '}
                      <b style={{ color: getColumnColor(investment.change) }}>
                        {formatDisplayNumber(investment.change)}đ(
                        {investment.percent}%)
                      </b>{' '}
                      và để cổ phiếu phục hồi sẽ cần tăng ít nhất{' '}
                      <b>{Math.abs(investment?.needPercent).toFixed(2)}%</b> so
                      với giá hiện tại.{' '}
                      {investment.zigZag.length &&
                      Number(investment.percent) < -4 ? (
                        <>
                          Theo dữ liệu giao dịch trong <b>12 tháng</b> gần nhất
                          chỉ có <b>{investment.zigZag.length}</b> lần{' '}
                          <b>{investment.machungkhoan}</b> tăng vượt mức{' '}
                          <b>{investment?.needPercent.toFixed(2)}%</b> theo giá
                          đóng cửa vào các chu kỳ:
                        </>
                      ) : null}
                    </p>
                  )}
                  {investment.zigZag.length &&
                  ((investment.change > 0 && investment.percent > 4) ||
                    (investment.change <= 0 && investment.percent < -4)) ? (
                    <ul className="list-disc ml-4 mt-2">
                      {investment?.zigZag?.map((zigZag) => (
                        <li key={zigZag.id}>
                          Từ{' '}
                          <b>
                            {parseDate(zigZag.fromDate)} -{' '}
                            {parseDate(zigZag.toDate)}
                          </b>{' '}
                          tăng <b>{zigZag.percent.toFixed(2)}%</b> từ{' '}
                          <b>{formatDisplayNumber(zigZag.from * 1000)}đ</b> đến{' '}
                          <b>{formatDisplayNumber(zigZag.to * 1000)}đ</b>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                  {investment?.news?.length ? (
                    <div className="mt-1">
                      <p className="font-bold">Tin liên quan</p>
                      <ul className="list-disc ml-6">
                        {investment.news.map((news) => (
                          <li key={news.id} className="mb-3">
                            <a
                              href={`${ROUTES.newsStockMarket}?id=${news.aliasCode}`}
                              target="_blank"
                              className="hover:underline transition ease-in-out duration-200 text-primary-main"
                              onClick={() =>
                                handleToggleRead(
                                  ![1, 5].includes(notification.status),
                                  notification.id,
                                  notification.type
                                )
                              }
                              title={news.title.replace(/&nbsp;/g, '')}
                            >
                              {news.title.replace(/&nbsp;/g, '')}
                            </a>{' '}
                            - Nguồn {news.source}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          </Box>
        </Card>
      );
    }
    return (
      <Card variant="outlined" className="w-full">
        <Box className="notification-header">
          {getICon(notification.type)}
          <div className="ml-auto">{notification.timeSend}</div>
        </Box>
        <Box className="ml-3 notification-content">
          <div>
            <p>
              Thị trường còn khoảng 30 phút nữa sẽ mở cửa ngày{' '}
              <b>{notification.timeSend.split(' ')[0]}</b>, Stockup tổng hợp một
              số thông tin nhà đầu tư có thể tham khảo trước khi thị trường mở
              cửa.
            </p>
            {otherInfo?.investments?.length ? (
              <div className="mt-4">
                {selectedInvestmentStock === 'general' ? (
                  <p className="font-bold">Danh mục đầu tư:</p>
                ) : null}
                <ul className="list-disc ml-5">
                  {otherInfo.investments.map((investment) => (
                    <li key={investment.machungkhoan} className="mb-2">
                      {investment.change > 0 ? (
                        <p>
                          <b>{investment.machungkhoan}</b>: tăng{' '}
                          <b
                            style={{ color: getColumnColor(investment.change) }}
                          >
                            +{formatDisplayNumber(investment.change)}đ( +
                            {investment.percent}%)
                          </b>{' '}
                          trên tổng giá trị đầu tư vào cổ phiếu, nhà đầu tư nên
                          theo dõi những biến động trong phiên để có những quyết
                          định phù hợp.{' '}
                          {investment.zigZag.length &&
                          investment.percent > 4 ? (
                            <>
                              Theo dữ liệu giao dịch trong <b>12 tháng</b> gần
                              nhất chỉ có <b>{investment.zigZag.length}</b> lần{' '}
                              <b>{investment.machungkhoan}</b> tăng vượt mức{' '}
                              <b>
                                {getMinMaxZizZag(investment.zigZag)[0].toFixed(
                                  2
                                )}
                                %
                              </b>{' '}
                              theo giá đóng cửa vào các chu kỳ:
                            </>
                          ) : null}
                        </p>
                      ) : (
                        <p>
                          <b>{investment.machungkhoan}</b>: giảm{' '}
                          <b
                            style={{ color: getColumnColor(investment.change) }}
                          >
                            {formatDisplayNumber(investment.change)}đ(
                            {investment.percent}%)
                          </b>{' '}
                          và để cổ phiếu phục hồi sẽ cần tăng ít nhất{' '}
                          <b>{Math.abs(investment?.needPercent).toFixed(2)}%</b>{' '}
                          so với giá hiện tại.{' '}
                          {investment.zigZag.length &&
                          investment.percent < -4 ? (
                            <>
                              Theo dữ liệu giao dịch trong <b>12 tháng</b> gần
                              nhất chỉ có <b>{investment.zigZag.length}</b> lần{' '}
                              <b>{investment.machungkhoan}</b> tăng vượt mức{' '}
                              {investment?.needPercent.toFixed(2)}% theo giá
                              đóng cửa vào các chu kỳ:
                            </>
                          ) : null}
                        </p>
                      )}
                      {investment.zigZag.length &&
                      ((investment.change > 0 && investment.percent > 4) ||
                        (investment.change <= 0 && investment.percent < -4)) ? (
                        <ul className="list-disc ml-4 mt-2">
                          {investment?.zigZag?.map((zigZag) => (
                            <li key={zigZag.id}>
                              Từ{' '}
                              <b>
                                {parseDate(zigZag.fromDate)} -{' '}
                                {parseDate(zigZag.toDate)}
                              </b>{' '}
                              tăng <b>{zigZag.percent.toFixed(2)}%</b> từ{' '}
                              <b>{formatDisplayNumber(zigZag.from * 1000)}đ</b>{' '}
                              đến{' '}
                              <b>{formatDisplayNumber(zigZag.to * 1000)}đ</b>
                            </li>
                          ))}
                        </ul>
                      ) : null}
                      {investment?.news?.length ? (
                        <div className="mt-1">
                          <p className="font-bold">Tin liên quan</p>
                          <ul className="list-disc ml-6">
                            {investment.news.map((news) => (
                              <li key={news.id} className="mb-3">
                                <a
                                  href={`${ROUTES.newsStockMarket}?id=${news.aliasCode}`}
                                  target="_blank"
                                  className="hover:underline transition ease-in-out duration-200 text-primary-main"
                                  onClick={() =>
                                    handleToggleRead(
                                      ![1, 5].includes(notification.status),
                                      notification.id,
                                      notification.type
                                    )
                                  }
                                  title={news.title.replace(/&nbsp;/g, '')}
                                >
                                  {news.title.replace(/&nbsp;/g, '')}
                                </a>{' '}
                                - Nguồn {news.source}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {otherInfo?.highlight?.length &&
            selectedInvestmentStock === 'general' ? (
              <div className="mt-4">
                <p>
                  Có một số cổ phiếu tiềm năng mà hiện chưa có trong danh mục
                  theo dõi của bạn như {renderHighlight(otherInfo.highlight)}{' '}
                  bạn có thể tham khảo và theo dõi diễn biến trong ngày.
                </p>
              </div>
            ) : null}
            {otherInfo.worldStockMarket?.length &&
            otherInfo.worldStockMarket?.[0]?.type &&
            selectedInvestmentStock === 'general' ? (
              <div className="mt-4">
                <p className="font-bold">Thị trường chứng khoán thế giới</p>
                <ul className="mt-2 list-disc ml-5">
                  {otherInfo.worldStockMarket.map((item, index) => {
                    if (!item?.name) {
                      return null;
                    }
                    if (item?.type === 'nasdaq') {
                      return (
                        <li key={item?.id || index} className="mb-1">
                          <b>{item.name}</b>:{' '}
                          <span
                            style={{
                              color: getColumnColor(item.changePercent),
                            }}
                          >
                            {formatDisplayNumber(item.price)}{' '}
                            {formatDisplayNumber(item.change)}(
                            {item.changePercent}%)
                          </span>{' '}
                          (cập nhật lúc{' '}
                          {dayjs(Number(item.date)).format(
                            'HH:mm:ss DD/MM/YYYY'
                          )}
                          ) - Nguồn {item.source}
                        </li>
                      );
                    }
                    return (
                      <li key={item?.id || index} className="mb-1">
                        <b>{item.name}</b>:{' '}
                        <span
                          style={{ color: getColumnColor(item.changePercent) }}
                        >
                          {formatDisplayNumber(item.change)}(
                          {item.changePercent}%)
                        </span>{' '}
                        (cập nhật lúc{' '}
                        {dayjs(Number(item.date)).format('HH:mm:ss DD/MM/YYYY')}
                        ) - Nguồn {item.source}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null}

            {otherInfo.commodityMarket?.length &&
            otherInfo.commodityMarket?.[0]?.type &&
            selectedInvestmentStock === 'general' ? (
              <div className="mt-4">
                <p className="font-bold">Thị trường hàng hóa</p>
                <ul className="mt-2 list-disc ml-5">
                  {otherInfo.commodityMarket.map((item, index) => {
                    if (!item?.name) {
                      return null;
                    }
                    if (item?.type === 'gold') {
                      return (
                        <li key={item?.id || index} className="mb-1">
                          <b>{item.name}</b>:{' '}
                          <span style={{ color: getColumnColor(item.change) }}>
                            {formatDisplayNumber(item.price)}
                          </span>{' '}
                          USD/Ounce (cập nhật lúc{' '}
                          {dayjs(Number(item.date)).format(
                            'HH:mm:ss DD/MM/YYYY'
                          )}
                          ) - Nguồn {item.source}
                        </li>
                      );
                    }
                    return (
                      <li key={item?.id || index} className="mb-1">
                        <b>{item.name}</b>:{' '}
                        <span
                          style={{ color: getColumnColor(item.changePercent) }}
                        >
                          {formatDisplayNumber(item.price)}{' '}
                          {formatDisplayNumber(item.change)}(
                          {item.changePercent}%)
                        </span>{' '}
                        (cập nhật lúc{' '}
                        {dayjs(Number(item.date)).format('HH:mm:ss DD/MM/YYYY')}
                        ) - Nguồn {item.source}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null}

            {otherInfo?.futuresContracts?.length &&
            otherInfo?.futuresContracts?.[0]?.type &&
            selectedInvestmentStock === 'general' ? (
              <div className="mt-4">
                <p className="font-bold">Hợp đồng Tương Lai</p>
                <ul className="mt-2 list-disc ml-5">
                  {otherInfo.futuresContracts.map((item, index) => {
                    if (!item?.name) {
                      return null;
                    }
                    return (
                      <li key={item?.id || index} className="mb-1">
                        <b>{item.name}</b>:{' '}
                        <span
                          style={{ color: getColumnColor(item.changePercent) }}
                        >
                          {formatDisplayNumber(item.price)}{' '}
                          {formatDisplayNumber(item.change)}(
                          {item.changePercent}%)
                        </span>{' '}
                        (cập nhật lúc{' '}
                        {dayjs(Number(item.date)).format('HH:mm:ss DD/MM/YYYY')}
                        ) - Nguồn {item.source}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null}

            {otherInfo.analysis[0]?.content &&
            selectedInvestmentStock === 'general' ? (
              <div className="mt-4">
                <p className="font-bold">Dự báo xu hướng VN-Index</p>
                <p className="mt-2 ml-2">
                  {renderContentItem(JSON.parse(otherInfo.analysis[0].content))}
                </p>
              </div>
            ) : null}
            <div></div>
          </div>
        </Box>
      </Card>
    );
  }

  if (selectedFollowStock === 'general') {
    return null;
  }

  if (!otherInfo.follows.length) {
    return null;
  }

  return (
    <Card variant="outlined" className="w-full">
      <Box className="notification-header">
        {getICon(notification.type)}
        <div className="ml-auto">{notification.timeSend}</div>
      </Box>
      <Box className="ml-3 notification-content">
        <div>
          <ul>
            {otherInfo.follows.map((stock) => (
              <li className="mb-2" key={stock?.machungkhoan}>
                <p>
                  <b>{stock?.machungkhoan}</b> giá đã giảm{' '}
                  <b style={{ color: getColumnColor(stock?.percent) }}>
                    {stock?.percent?.toFixed(2)}%
                  </b>{' '}
                  so với đỉnh gần nhất.
                </p>
                {stock?.news?.length ? (
                  <div className="mt-2">
                    <p className="mb-1 font-bold">Tin liên quan:</p>
                    <ul className="list-disc ml-6">
                      {stock.news.map((news) => (
                        <li key={news.id} className="mb-3">
                          <a
                            href={`${ROUTES.newsStockMarket}?id=${news.aliasCode}`}
                            target="_blank"
                            className="hover:underline transition ease-in-out duration-200 text-primary-main"
                            onClick={() =>
                              handleToggleRead(
                                ![1, 5].includes(notification.status),
                                notification.id,
                                notification.type
                              )
                            }
                            title={news.title.replace(/&nbsp;/g, '')}
                          >
                            {news.title.replace(/&nbsp;/g, '')}
                          </a>{' '}
                          - Nguồn {news.source}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      </Box>
    </Card>
  );
}

const getCateFollow = (follows = [], catId = '') => {
  return follows.filter((f) => Number(f.id) === Number(catId));
};

function Notification({
  notification,
  handleToggleRead,
  isInvestmentCategoriesLoading,
  authenticated,
  investmentCategories,
  followCompanies,
  saveInvestmentCategory,
  deleteInvestmentCategory,
  investmentCategoriesMessage,
  isOpeningBookmark,
  handleSetIsOpeningBookmark,
  isDarkMode,
  selectedFollowStock,
  selectedInvestmentStock,
  currentTab,
  selectedFollowCategory,
}) {
  const getContent = () => {
    if (notification.type === trackingNotificationType.newsUpdate) {
      return (
        <Card variant="outlined" className="w-full">
          <Box className="notification-header">
            <Tooltip
              title={
                [1, 5].includes(notification.status)
                  ? 'Đánh dấu đã đọc'
                  : 'Đánh dấu chưa đọc'
              }
            >
              <div className="flex md:hidden">
                <Radio
                  className="dot-badge "
                  color="error"
                  checked={[1, 5].includes(notification.status)}
                  onClick={() =>
                    handleToggleRead(
                      ![1, 5].includes(notification.status),
                      notification.id,
                      notification.type
                    )
                  }
                />
              </div>
            </Tooltip>
            {getICon(notification.type)}
            <div className="ml-auto">{notification.timeSend}</div>
          </Box>
          <Box className="ml-3 notification-content">
            {Parser(notification.message)}
          </Box>
        </Card>
      );
    }

    if (notification.type === trackingNotificationType.Indicator) {
      return (
        <Card variant="outlined" className="w-full">
          <Box className="notification-header">
            <Tooltip
              title={
                [1, 5].includes(notification.status)
                  ? 'Đánh dấu đã đọc'
                  : 'Đánh dấu chưa đọc'
              }
            >
              <div className="flex md:hidden">
                <Radio
                  className="dot-badge "
                  color="error"
                  checked={[1, 5].includes(notification.status)}
                  onClick={() =>
                    handleToggleRead(
                      ![1, 5].includes(notification.status),
                      notification.id,
                      notification.type
                    )
                  }
                />
              </div>
            </Tooltip>
            {getICon(notification.type)}
            <div className="ml-auto">{notification.timeSend}</div>
          </Box>
          <Box className="ml-3 notification-content">
            <Typography component="div">
              <div className="text-sm">
                Bộ lọc của Stockup nhận thấy có một số cổ phiếu tiềm năng mà bạn
                có thể xem xét để đưa vào danh mục theo dõi của mình:
              </div>
              <ul className="ml-6 list-disc">
                {notification.otherInfo.stocks.map((stock) => (
                  <li className="text-sm font-bold" key={stock}>
                    <div className="flex items-center">
                      <span className="mr-1">{stock}</span>
                      <WrapperBookmark
                        code={stock}
                        isInvestmentCategoriesLoading={
                          isInvestmentCategoriesLoading
                        }
                        authenticated={authenticated}
                        investmentCategories={investmentCategories}
                        followCompanies={followCompanies}
                        saveInvestmentCategory={saveInvestmentCategory}
                        deleteInvestmentCategory={deleteInvestmentCategory}
                        investmentCategoriesMessage={
                          investmentCategoriesMessage
                        }
                        isOpeningBookmark={isOpeningBookmark}
                        handleSetIsOpeningBookmark={handleSetIsOpeningBookmark}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            </Typography>
          </Box>
        </Card>
      );
    }

    if (notification.type === trackingNotificationType.Investment) {
      const otherInfo = notification.otherInfo;
      return (
        <Card variant="outlined" className="w-full">
          <Box className="notification-header">
            <Tooltip
              title={
                [1, 5].includes(notification.status)
                  ? 'Đánh dấu đã đọc'
                  : 'Đánh dấu chưa đọc'
              }
            >
              <div className="flex md:hidden">
                <Radio
                  className="dot-badge "
                  color="error"
                  checked={[1, 5].includes(notification.status)}
                  onClick={() =>
                    handleToggleRead(
                      ![1, 5].includes(notification.status),
                      notification.id,
                      notification.type
                    )
                  }
                />
              </div>
            </Tooltip>
            {getICon(notification.type)}
            <div className="ml-auto">{notification.timeSend}</div>
          </Box>
          <Box className="ml-3 notification-content">
            <div className="text-sm">
              <p>
                Cổ phiếu <b>{otherInfo.stock}</b> mà bạn vừa thêm vào danh mục
                đầu tư có một số thông tin như sau:
              </p>
              <p className="font-bold my-2">
                Kết quả kinh doanh 4 quý gần nhất
              </p>
              <div>
                <FinancialChart
                  financialData={otherInfo.financials}
                  isDarkMode={isDarkMode}
                />
              </div>
              <p className="font-bold mt-2">
                Khối lượng giao dịch trung bình trong {otherInfo.day} ngày gần
                nhất
              </p>
              <p>
                {formatDisplayNumber(
                  Number(otherInfo.tradingVolume).toFixed(0)
                )}{' '}
                cổ phiếu/ngày.
              </p>
              {Number(otherInfo.tradingVolume) < 10000 ? (
                <>
                  <p className="font-bold my-2"> Đáng quan tâm</p>
                  <p>
                    Vì cổ phiếu <b>{otherInfo.stock}</b> có khối lượng giao dịch
                    thấp nên khi bạn muốn bán cổ phiếu này thì có thể không có
                    quá nhiều nhà đầu tư sẵn sàng mua với mức giá phù hợp. Bạn
                    nên cân nhắc khoản đầu tư này.
                  </p>
                </>
              ) : null}
            </div>
          </Box>
        </Card>
      );
    }

    if (notification.type === trackingNotificationType.GENERAL_FOLLOW) {
      const otherInfo = notification.otherInfo;
      if (selectedFollowStock === 'general') {
        return (
          <Card variant="outlined" className="w-full">
            <Box className="notification-header">
              <Tooltip
                title={
                  [1, 5].includes(notification.status)
                    ? 'Đánh dấu đã đọc'
                    : 'Đánh dấu chưa đọc'
                }
              >
                <div className="flex md:hidden">
                  <Radio
                    className="dot-badge "
                    color="error"
                    checked={[1, 5].includes(notification.status)}
                    onClick={() =>
                      handleToggleRead(
                        ![1, 5].includes(notification.status),
                        notification.id,
                        notification.type
                      )
                    }
                  />
                </div>
              </Tooltip>
              {getICon(notification.type)}
              <div className="ml-auto">{notification.timeSend}</div>
            </Box>
            <Box className="ml-3 notification-content">
              <div>
                <ul className="list-disc ml-4 mt-2">
                  {getCateFollow(otherInfo, selectedFollowCategory?.id)?.map(
                    (catFollow) => (
                      <li key={catFollow.id}>
                        <b>{catFollow.name}</b>
                        <ul className="list-disc ml-4">
                          {catFollow.stocks.map((stock) => (
                            <li className="mb-2" key={stock?.machungkhoan}>
                              <p>
                                <b>{stock?.machungkhoan}</b> giá đã giảm{' '}
                                <b
                                  style={{
                                    color: getColumnColor(stock?.percent),
                                  }}
                                >
                                  {stock?.percent?.toFixed(2)}%
                                </b>{' '}
                                so với đỉnh gần nhất.
                              </p>
                              {stock?.news?.length ? (
                                <div className="mt-2">
                                  <p className="mb-1 font-bold">
                                    Tin liên quan:
                                  </p>
                                  <ul className="list-disc ml-6">
                                    {stock.news.map((news) => (
                                      <li key={news.id} className="mb-3">
                                        <a
                                          href={`${ROUTES.newsStockMarket}?id=${news.aliasCode}`}
                                          target="_blank"
                                          className="hover:underline transition ease-in-out duration-200 text-primary-main"
                                          onClick={() =>
                                            handleToggleRead(
                                              ![1, 5].includes(
                                                notification.status
                                              ),
                                              notification.id,
                                              notification.type
                                            )
                                          }
                                          title={news.title.replace(
                                            /&nbsp;/g,
                                            ''
                                          )}
                                        >
                                          {news.title.replace(/&nbsp;/g, '')}
                                        </a>{' '}
                                        - Nguồn {news.source}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              ) : null}
                            </li>
                          ))}
                        </ul>
                      </li>
                    )
                  )}
                </ul>
              </div>
            </Box>
          </Card>
        );
      }
      return (
        <Card variant="outlined" className="w-full">
          <Box className="notification-header">
            <Tooltip
              title={
                [1, 5].includes(notification.status)
                  ? 'Đánh dấu đã đọc'
                  : 'Đánh dấu chưa đọc'
              }
            >
              <div className="flex md:hidden">
                <Radio
                  className="dot-badge "
                  color="error"
                  checked={[1, 5].includes(notification.status)}
                  onClick={() =>
                    handleToggleRead(
                      ![1, 5].includes(notification.status),
                      notification.id,
                      notification.type
                    )
                  }
                />
              </div>
            </Tooltip>
            {getICon(notification.type)}
            <div className="ml-auto">{notification.timeSend}</div>
          </Box>
          <Box className="ml-3 notification-content">
            <div>
              <ul className="list-disc ml-4">
                {otherInfo.follows.stocks.map((stock) => (
                  <li className="mb-2" key={stock?.machungkhoan}>
                    <p>
                      <b>{stock?.machungkhoan}</b> giá đã giảm{' '}
                      <b style={{ color: getColumnColor(stock?.percent) }}>
                        {stock?.percent?.toFixed(2)}%
                      </b>{' '}
                      so với đỉnh gần nhất.
                    </p>
                    {stock?.news?.length ? (
                      <div className="mt-2">
                        <p className="mb-1 font-bold">Tin liên quan:</p>
                        <ul className="list-disc ml-6">
                          {stock.news.map((news) => (
                            <li key={news.id} className="mb-3">
                              <a
                                href={`${ROUTES.newsStockMarket}?id=${news.aliasCode}`}
                                target="_blank"
                                className="hover:underline transition ease-in-out duration-200 text-primary-main"
                                onClick={() =>
                                  handleToggleRead(
                                    ![1, 5].includes(notification.status),
                                    notification.id,
                                    notification.type
                                  )
                                }
                                title={news.title.replace(/&nbsp;/g, '')}
                              >
                                {news.title.replace(/&nbsp;/g, '')}
                              </a>{' '}
                              - Nguồn {news.source}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          </Box>
        </Card>
      );
    }

    if (notification.type === trackingNotificationType.GENERAL) {
      const otherInfo = notification.otherInfo;
      return (
        <GeneralNotification
          otherInfo={otherInfo}
          notification={notification}
          currentTab={currentTab}
          selectedFollowStock={selectedFollowStock}
          selectedInvestmentStock={selectedInvestmentStock}
        />
      );
    }

    return (
      <NotificationItem
        notification={notification}
        handleToggleRead={handleToggleRead}
        isDarkMode={isDarkMode}
        variant="toggleRead"
      />
    );
  };

  const renderRadio = () => {
    if (
      !notification.message &&
      notification.type === trackingNotificationType.newsUpdate
    ) {
      return;
    }
    return (
      <Tooltip
        title={
          [1, 5].includes(notification.status)
            ? 'Đánh dấu đã đọc'
            : 'Đánh dấu chưa đọc'
        }
      >
        <div className="hidden md:flex">
          <Radio
            className="dot-badge "
            color="error"
            checked={[1, 5].includes(notification.status)}
            onClick={() =>
              handleToggleRead(
                ![1, 5].includes(notification.status),
                notification.id,
                notification.type
              )
            }
          />
        </div>
      </Tooltip>
    );
  };

  return (
    <Styles.NotificationItem
      className={classNames(
        'flex p-3 relative text-sm mb-3',
        [1, 5].indexOf(notification.status) !== -1 ? 'unread' : ''
      )}
      key={`investment-tab-${notification.id}`}
    >
      {renderRadio()}
      {getContent()}
    </Styles.NotificationItem>
  );
}

function Stocks({ onClick, items, unreadNotifications, selectedItem }) {
  if (!items?.length) {
    return null;
  }
  return (
    <Styles.StockWrapper className="w-full ">
      <div className="flex items-center mb-4">
        {items?.map((investment) => {
          const item = unreadNotifications.find(
            (u) => u.stock === investment.machungkhoan
          );
          return (
            <Button
              key={`${investment.machungkhoan}`}
              onClick={() => onClick(investment.machungkhoan)}
              variant={
                selectedItem === investment.machungkhoan
                  ? 'contained'
                  : 'outlined'
              }
              className="mr-3 whitespace-nowrap"
            >
              {investment.machungkhoan === 'general'
                ? 'Thông báo chung'
                : investment.machungkhoan}{' '}
              {Number(item?.total) > 0 ? `(${item?.total || 0})` : null}
            </Button>
          );
        })}
      </div>
    </Styles.StockWrapper>
  );
}

const NotificationDrawer = () => {
  const {
    isInvestmentNotificationsLoading,
    investmentStocks,
    investmentNotifications,
    investmentUnreadNotifications,
    selectedInvestmentStock,

    isFollowCompanyLoading,
    isFollowLoading,
    followNotifications,
    followUnreadNotifications,
    followStocks,
    selectedFollowCategory,
    setSelectedFollowCategory,
    handleFollowCategoryChange,
    selectedFollowStock,
    setSelectedFollowStock,

    isOtherLoading,
    otherNotifications,
    otherUnreadNotifications,

    handleSelectedStock,
    handlePageChange,
    handleUpdateReadNotification,
    handleTabChange,
    handleOpenFilterMenu,
    handleCloseFilterMenu,
    handleKeywordChange,
    handleTypeChange,
    handleToggleRead,
    setCurrentTab,

    anchorElFilter,
    currentTab,
    keyword,
    selectedType,
    dataSnackBar,
    isUnreadSelected,
    isPageLoading,
  } = useNotification();
  const { isDarkMode } = useToggleTheme();

  const {
    isInvestmentCategoriesLoading,
    authenticated,
    investmentCategories,
    followCompanies,
    saveInvestmentCategory,
    deleteInvestmentCategory,
    investmentCategoriesMessage,
    isOpeningBookmark,
    handleSetIsOpeningBookmark,
  } = useInvestmentCategories();

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const handleSnackbarClose = () => {
    setOpenSnackbar(false);
  };

  const dispatch = useAppDispatch();
  const { open, activeTab, category } = useAppSelector(
    (state) => state.notification
  );
  const [anchorEl, setAnchorEl] = useState(null);

  useEffect(() => {
    if (open) {
      handleTabChange(null, activeTab);
      let followStock = followStocks.find((follow) => follow.id === category);
      if (activeTab === 1 && !followStock) {
        followStock = followStocks.find((follow) => follow.isDefault === 1);
      }
      if (followStock) {
        setSelectedFollowCategory(followStock);
        setSelectedFollowStock(followStock?.items?.[0]?.machungkhoan);
      }
    }
  }, [open, activeTab, category, followStocks]);

  const onClose = () => {
    dispatch(setCloseNotificationDrawer());
  };

  const getTotalUnreadInvestmentNotification = () => {
    let total = 0;
    investmentStocks?.forEach((investment) => {
      const item = investmentUnreadNotifications.find(
        (u) => u.stock === investment.machungkhoan
      );
      total += Number(item?.total) || 0;
    });
    return total ? ` (${total})A` : '';
  };

  const getTotalUnreadOtherNotification = () => {
    return otherUnreadNotifications?.length
      ? ` (${otherUnreadNotifications?.length})`
      : '';
  };

  const getUnreadIds = (items) => {
    return items
      .filter((i) =>
        [
          notificationStatusType.New,
          notificationStatusType.Sent,
          notificationStatusType.View,
        ].includes(i.status)
      )
      .map((i) => i.id);
  };

  const handleOpenFollowCategoryClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleFollowCategoryClose = () => {
    setAnchorEl(null);
  };

  const openFollowCategory = Boolean(anchorEl);
  const id = openFollowCategory ? 'follow-category-popover' : undefined;

  const handleFollowCategoryItemClick = (follow) => {
    setCurrentTab(1);
    handleFollowCategoryChange(follow);
    if (follow?.items?.length) {
      handleSelectedStock(follow.items[0].machungkhoan, 1, follow?.id);
    } else {
      handleSelectedStock('', 1, follow?.id);
    }
    handleFollowCategoryClose();
  };

  const getFollowTabLabel = () => {
    if (!selectedFollowCategory?.name && !followStocks?.length) {
      return 'Danh mục theo dõi';
    }

    return (
      <div className="relative">
        <Loading isLoading={isFollowCompanyLoading} small isHorizontal />
        <Styles.FakeDropdown
          aria-describedby={id}
          role="button"
          onClick={handleOpenFollowCategoryClick}
          className={classNames(openFollowCategory ? 'opened' : '')}
        >
          {!selectedFollowCategory?.name ? (
            <span>
              Danh mục theo dõi{' '}
              {followStocks?.length ? '(' + followStocks?.length + ')' : ''}
            </span>
          ) : (
            <span>
              {selectedFollowCategory?.name}&nbsp;
              {getTotalNotificationByFollowCategory(
                followStocks,
                selectedFollowCategory?.id,
                followUnreadNotifications
              )}
            </span>
          )}
          <ArrowDropDownIcon />
        </Styles.FakeDropdown>
        <Popover
          id={id}
          open={openFollowCategory}
          anchorEl={anchorEl}
          onClose={handleFollowCategoryClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
        >
          <MenuList
            autoFocusItem={openFollowCategory}
            id="composition-menu"
            aria-labelledby="composition-button"
            className="max-h-[250px] overflow-y-auto"
          >
            {followStocks?.map((follow) => (
              <MenuItem
                key={follow.id}
                onClick={() => handleFollowCategoryItemClick(follow)}
              >
                {follow.name}&nbsp;
                {getTotalNotificationByFollowCategory(
                  followStocks,
                  follow.id,
                  followUnreadNotifications
                )}
              </MenuItem>
            ))}
          </MenuList>
        </Popover>
      </div>
    );
  };

  const debounceHandleKeywordChange = debounce(handleKeywordChange, 500);

  const selectParamToRenderStocks = () => {
    if (currentTab == 0) {
      return {
        stocks: investmentStocks,
        unreadNotifications: investmentUnreadNotifications,
        selectedStock: selectedInvestmentStock,
      };
    }
    if (currentTab == 1) {
      return {
        stocks: selectedFollowCategory?.items,
        unreadNotifications: followUnreadNotifications,
        selectedStock: selectedFollowStock,
      };
    }
    if (currentTab == 2) {
      return null;
    }
  };

  const renderFilterAndSearch = () => {
    return (
      <Box className="filter-block flex items-center mb-0 md:mb-4 mt-4 md:mt-0 flex-col ssm:flex-row">
        <div className="flex mb-2 ssm:mb-0">
          <Input
            defaultValue={keyword}
            name="search"
            placeholder="Tìm kiếm"
            className="search"
            onChange={debounceHandleKeywordChange}
            useDebounce={false}
          />
          <IconButton onClick={handleOpenFilterMenu}>
            <FilterListIcon />
          </IconButton>
          <Styles.Menu
            id="filter-appbar"
            anchorEl={anchorElFilter}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorElFilter)}
            onClose={handleCloseFilterMenu}
          >
            <MenuItem
              className={classNames(
                selectedType === '' && !isUnreadSelected ? 'selected' : ''
              )}
              onClick={() => handleTypeChange('')}
            >
              <ViewComfyAltIcon />{' '}
              <Typography component="span" className="menu-text">
                Tất cả
              </Typography>
            </MenuItem>
            <MenuItem
              className={classNames(isUnreadSelected ? 'selected' : '')}
              onClick={() => handleTypeChange('unread')}
            >
              <PlaylistRemoveIcon />{' '}
              <Typography component="span" className="menu-text">
                Chưa đọc
              </Typography>
            </MenuItem>
            <MenuItem
              className={classNames(
                selectedType === trackingNotificationType.system
                  ? 'selected'
                  : ''
              )}
              onClick={() => handleTypeChange(trackingNotificationType.system)}
            >
              {getICon('')}
            </MenuItem>
            <MenuItem
              className={classNames(
                selectedType === trackingNotificationType.priceChange
                  ? 'selected'
                  : ''
              )}
              onClick={() =>
                handleTypeChange(trackingNotificationType.priceChange)
              }
            >
              {getICon(trackingNotificationType.priceChange)}
            </MenuItem>
            <MenuItem
              className={classNames(
                selectedType === trackingNotificationType.companyExercise
                  ? 'selected'
                  : ''
              )}
              onClick={() =>
                handleTypeChange(trackingNotificationType.companyExercise)
              }
            >
              {getICon(trackingNotificationType.companyExercise)}
            </MenuItem>
            <MenuItem
              className={classNames(
                selectedType === trackingNotificationType.newsUpdate
                  ? 'selected'
                  : ''
              )}
              onClick={() =>
                handleTypeChange(trackingNotificationType.newsUpdate)
              }
            >
              {getICon(trackingNotificationType.newsUpdate)}
            </MenuItem>
          </Styles.Menu>
        </div>
        <Button
          className="btn-mask-ask-read text-white"
          onClick={() => {
            if (currentTab == 0) {
              handleUpdateReadNotification(
                getUnreadIds(
                  investmentNotifications[selectedInvestmentStock].data
                )
              );
            }
            if (currentTab == 1) {
              handleUpdateReadNotification(
                getUnreadIds(followNotifications[selectedFollowStock].data)
              );
            }
            if (currentTab == 2) {
              handleUpdateReadNotification(
                getUnreadIds(otherNotifications.data)
              );
            }
          }}
        >
          <PlaylistAddCheckIcon style={{ color: 'white' }} />{' '}
          <span className="ml-1">Đánh dấu đã đọc</span>
        </Button>
      </Box>
    );
  };

  const renderStockList = () => {
    const params = selectParamToRenderStocks();
    return (
      <Box className="mt-4">
        <div
          className={classNames(
            'flex justify-between flex-col md:flex-row list-stocks',
            isPC
              ? params && params.stocks?.length > 5
                ? 'overflow-y-scroll'
                : ''
              : ''
          )}
        >
          <div className="mr-0 md:mr-6 overflow-y-auto">
            {params && (
              <Stocks
                onClick={handleSelectedStock}
                items={params.stocks}
                unreadNotifications={params.unreadNotifications}
                selectedItem={params.selectedStock}
              />
            )}
          </div>
          {renderFilterAndSearch()}
        </div>
      </Box>
    );
  };

  const isPC = !/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );

  useEffect(() => {
    if (dataSnackBar?.show) {
      setOpenSnackbar(true);
    }
  }, [dataSnackBar]);

  return (
    <Styles.Drawer anchor="right" open={open} onClose={onClose}>
      <Loading isLoading={isPageLoading} />
      <CustomSnackbar
        open={openSnackbar}
        message={dataSnackBar?.message}
        isError={dataSnackBar?.isError}
        onClose={handleSnackbarClose}
      />
      <Box className="drawer-main-content pt-3">
        <Box className="flex justify-between">
          <Box className=" hidden title-block md:flex items-center min-w-[130px]  ">
            <IconButton onClick={onClose}>
              <CloseOutlinedIcon />
            </IconButton>
            <Typography className="title">Thông báo</Typography>
          </Box>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={currentTab}
              onChange={handleTabChange}
              aria-label="basic tabs example"
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab
                label={`Danh mục đầu tư${getTotalUnreadInvestmentNotification()}`}
                {...a11yProps(0)}
              />
              <Tab label={getFollowTabLabel()} {...a11yProps(1)} />
              <Tab
                label={`Khác ${getTotalUnreadOtherNotification()}`}
                {...a11yProps(2)}
              />
            </Tabs>
          </Box>
        </Box>
        {renderStockList()}
        <Box className="main-tab-content">
          <TabPanel value={currentTab} index={0}>
            <div className="w-full relative notification-main-content">
              {isInvestmentNotificationsLoading ? (
                <Loading isLoading={isInvestmentNotificationsLoading} />
              ) : (
                <>
                  {selectedInvestmentStock &&
                  investmentNotifications[selectedInvestmentStock]?.data
                    ?.length ? (
                    <>
                      {transferNotificationData(
                        investmentNotifications[selectedInvestmentStock].data,
                        selectedInvestmentStock
                      )?.map((notification) => (
                        <Notification
                          key={`investmentNotifications-investment-tab-${notification.id}`}
                          notification={notification}
                          handleToggleRead={handleToggleRead}
                          isDarkMode={isDarkMode}
                          selectedFollowStock={selectedFollowStock}
                          selectedInvestmentStock={selectedInvestmentStock}
                          currentTab={currentTab}
                        />
                      ))}
                      <div className="py-3 mb-1">
                        <Pagination
                          totalPage={
                            investmentNotifications[selectedInvestmentStock]
                              .totalPage
                          }
                          onChange={handlePageChange}
                          defaultPage={
                            investmentNotifications[selectedInvestmentStock]
                              .currentPage
                          }
                        />
                      </div>
                    </>
                  ) : (
                    <div className="text-center font-semibold text-base my-9">
                      Không có dữ liệu
                    </div>
                  )}
                </>
              )}
            </div>
          </TabPanel>
          <TabPanel value={currentTab} index={1}>
            <div className="w-full relative notification-main-content">
              {isFollowLoading ? (
                <Loading isLoading={isFollowLoading} />
              ) : (
                <>
                  {selectedFollowStock &&
                  followNotifications[selectedFollowStock]?.data?.length ? (
                    <>
                      {transferNotificationData(
                        followNotifications[selectedFollowStock].data,
                        selectedFollowStock
                      )?.map((notification) => (
                        <Notification
                          key={`followNotifications-investment-tab-${notification.id}`}
                          notification={notification}
                          handleToggleRead={handleToggleRead}
                          selectedFollowStock={selectedFollowStock}
                          selectedInvestmentStock={selectedInvestmentStock}
                          currentTab={currentTab}
                          selectedFollowCategory={selectedFollowCategory}
                        />
                      ))}
                      <div className="py-3 mb-1">
                        <Pagination
                          totalPage={
                            followNotifications[selectedFollowStock].totalPage
                          }
                          onChange={handlePageChange}
                          defaultPage={
                            followNotifications[selectedFollowStock].currentPage
                          }
                        />
                      </div>
                    </>
                  ) : (
                    <div className="text-center font-semibold text-base my-9">
                      Không có dữ liệu
                    </div>
                  )}
                </>
              )}
            </div>
          </TabPanel>
          <TabPanel value={currentTab} index={2}>
            <div className="w-full relative notification-main-content">
              {isOtherLoading ? (
                <Loading isLoading={isOtherLoading} />
              ) : (
                <>
                  {otherNotifications?.data?.length ? (
                    <>
                      {transferNotificationData(otherNotifications.data)?.map(
                        (notification) => (
                          <Notification
                            key={`otherNotifications-investment-tab-${notification.id}`}
                            notification={notification}
                            handleToggleRead={handleToggleRead}
                            isInvestmentCategoriesLoading={
                              isInvestmentCategoriesLoading
                            }
                            authenticated={authenticated}
                            investmentCategories={investmentCategories}
                            followCompanies={followCompanies}
                            saveInvestmentCategory={saveInvestmentCategory}
                            deleteInvestmentCategory={deleteInvestmentCategory}
                            investmentCategoriesMessage={
                              investmentCategoriesMessage
                            }
                            isOpeningBookmark={investmentCategoriesMessage}
                            handleSetIsOpeningBookmark={
                              handleSetIsOpeningBookmark
                            }
                            currentTab={currentTab}
                          />
                        )
                      )}
                      <div className="py-3 mb-1">
                        <Pagination
                          totalPage={otherNotifications.totalPage}
                          onChange={handlePageChange}
                          defaultPage={otherNotifications.currentPage}
                        />
                      </div>
                    </>
                  ) : (
                    <div className="text-center font-semibold text-base my-9">
                      Không có dữ liệu
                    </div>
                  )}
                </>
              )}
            </div>
          </TabPanel>
        </Box>
      </Box>
    </Styles.Drawer>
  );
};

export default NotificationDrawer;
